import { useNavigate } from 'react-router-dom';
import { CalendarX, Timer } from 'lucide-react';
import { DashboardAlert } from '../utils/dashboardData';

/**
 * L'unique surface qui dit quoi faire avant ce soir.
 *
 * Une seule bande, triée par gravité : deux zones du même genre séparées par un
 * seuil d'urgence invisible apprennent à ignorer les deux, et une bande vide ne
 * voudrait plus jamais dire « rien à faire ». Le composant ne calcule rien, il
 * reçoit des alertes déjà construites.
 */

/** Au-delà, la bande cesse d'être une liste d'actions et devient un mur. */
const MAX_VISIBLE = 4;

const ICONS = {
  timer: Timer,
  calendarX: CalendarX,
};

interface Props {
  alerts: DashboardAlert[];
}

const AlertBand = ({ alerts }: Props) => {
  const navigate = useNavigate();

  if (alerts.length === 0) return null;

  const visible = alerts.slice(0, MAX_VISIBLE);
  const hidden = alerts.length - visible.length;

  return (
    <div className="flex flex-col gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-700">
      {visible.map((alert) => {
        const Icon = ICONS[alert.icon];
        return (
          <p
            key={alert.id}
            className={`flex items-start gap-2 ${
              alert.level === 'critical' ? 'text-[#BB0F33]' : ''
            }`}
          >
            <Icon size={16} className="mt-0.5 shrink-0" />
            {alert.text}
          </p>
        );
      })}

      {hidden > 0 && (
        <p className="text-xs">
          +{hidden} autre{hidden > 1 ? 's' : ''} point{hidden > 1 ? 's' : ''} à
          vérifier.
        </p>
      )}

      {/* Un seul lien pour toute la bande : une action par ligne transformerait
          la zone d'alerte en barre de navigation. */}
      <button
        type="button"
        onClick={() => navigate('/workshops')}
        className="mt-1 self-start text-sm text-[#623413] hover:underline"
      >
        Gérer les ateliers
      </button>
    </div>
  );
};

export default AlertBand;
